import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../../integrations/supabase/client';
import { lmsUrl } from '../../lib/surface';
import {
  claimPendingFreeCourse,
  getPendingFreeCourse,
  PENDING_FREE_COURSE_METADATA_KEY,
  rememberPendingFreeCourse,
} from '../../lib/free-enrollment';
import type { User as SupabaseUser } from '@supabase/supabase-js';

export interface LmsUser {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role: 'STUDENT' | 'ADMIN' | 'REVOKED';
  enrolledCourses: string[];
}

interface AuthContextType {
  user: LmsUser | null;
  supabaseUser: SupabaseUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEmailVerified: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (userData: SignupData) => Promise<{ needsConfirmation: boolean }>;
  logout: () => Promise<void>;
  updateProfile: (input: { name: string; phone: string }) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
}

interface SignupData {
  name: string;
  email: string;
  password: string;
  phone: string;
  courseId?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchProfile(userId: string) {
  const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
  return data;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<LmsUser | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const sbUser = session?.user ?? null;
      setSupabaseUser(sbUser);
      if (!sbUser) {
        setUser(null);
        return;
      }
      // A free-course intent may be waiting from the public catalogue. Claim
      // before loading enrollments so the new classroom appears immediately.
      const urlPendingCourse = new URLSearchParams(window.location.search).get('pending_course');
      if (urlPendingCourse) rememberPendingFreeCourse(urlPendingCourse);
      const claimedCourse = await claimPendingFreeCourse(sbUser).catch(() => null);
      if (claimedCourse && urlPendingCourse) {
        const clean = new URL(window.location.href);
        clean.searchParams.delete('pending_course');
        window.history.replaceState({}, '', `${clean.pathname}${clean.search}${clean.hash}`);
      }

      // Profile may not exist yet if migration not applied — fall back to metadata
      const profile = await fetchProfile(sbUser.id).catch(() => null);
      let enrollments: { course_id: string }[] | null = null;
      try {
        const res = await supabase.from('enrollments').select('course_id').eq('user_id', sbUser.id).eq('status', 'active');
        enrollments = (res.data ?? null) as unknown as { course_id: string }[] | null;
      } catch {
        enrollments = null;
      }
      setUser({
        id: sbUser.id,
        name:
          (profile as { full_name?: string } | null)?.full_name ||
          (sbUser.user_metadata?.full_name as string) ||
          sbUser.email?.split('@')[0] ||
          'Student',
        email: sbUser.email || '',
        phone: (profile as { phone?: string } | null)?.phone ?? (sbUser.user_metadata?.phone as string) ?? null,
        role: ((profile as { role?: string } | null)?.role as LmsUser['role']) || 'STUDENT',
        enrolledCourses: (enrollments as unknown as { course_id: string }[] | null)?.map((e) => e.course_id) ?? [],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
    const { data: listener } = supabase.auth.onAuthStateChange(() => {
      // Defer async Supabase work out of the auth callback to avoid the auth lock.
      setTimeout(() => { void loadSession(); }, 0);
    });
    return () => listener.subscription.unsubscribe();
  }, [loadSession]);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
    await loadSession();
  };

  const signup = async (userData: SignupData) => {
    const pendingCourseId = userData.courseId?.trim() || null;
    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          full_name: userData.name,
          phone: userData.phone,
          ...(pendingCourseId ? { [PENDING_FREE_COURSE_METADATA_KEY]: pendingCourseId } : {}),
        },
        // Keep a query-string fallback for auth setups that fall back to the
        // project Site URL instead of the requested LMS redirect.
        emailRedirectTo: lmsUrl(
          pendingCourseId ? `/dashboard?pending_course=${encodeURIComponent(pendingCourseId)}` : '/dashboard',
        ),
      },
    });
    if (error) throw new Error(error.message);
    await loadSession();
    return { needsConfirmation: !data.session };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSupabaseUser(null);
  };

  const updateProfile = async (input: { name: string; phone: string }) => {
    if (!user) throw new Error('You must be signed in.');
    const { error } = await supabase
      .from('profiles')
      .update({ full_name: input.name.trim(), phone: input.phone.trim() || null, updated_at: new Date().toISOString() })
      .eq('id', user.id);
    if (error) throw new Error(error.message);
    const { error: authError } = await supabase.auth.updateUser({
      data: { full_name: input.name.trim(), phone: input.phone.trim() },
    });
    if (authError) throw new Error(authError.message);
    await loadSession();
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw new Error(error.message);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: lmsUrl('/reset-password'),
    });
    if (error) throw new Error(error.message);
  };

  const resendVerification = async (email: string) => {
    const pendingCourse = getPendingFreeCourse();
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
      options: {
        emailRedirectTo: lmsUrl(
          pendingCourse ? `/dashboard?pending_course=${encodeURIComponent(pendingCourse)}` : '/dashboard',
        ),
      },
    });
    if (error) throw new Error(error.message);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        supabaseUser,
        isAuthenticated: !!supabaseUser,
        isAdmin: user?.role === 'ADMIN',
        isEmailVerified: !!supabaseUser?.email_confirmed_at,
        loading,
        login,
        signup,
        logout,
        updateProfile,
        updatePassword,
        resetPassword,
        resendVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
